<script lang="ts">
    import { t } from 'svelte-i18n';
    import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
    import { ProductApi } from '$lib/api/productApi.js';
    import { page } from '$app/stores';
    import type { ApiReturnType, ApiSchemas } from '$lib/api/shopwareApi.js';
    import { usePropertiesContext, useShopContext } from '$lib/context.js';
    import type { ApiRequestParams } from '$lib/api/shopwareApi.js';
    import { CartApi } from '$lib/api/cartApi.js';
    import { setContext } from 'svelte';
    import { writable } from 'svelte/store';
    import { createProductTitle, extractLine } from '$lib/util/productUtil.js';
    import { ADD_TO_CART_CONTEXT_KEY, type AddToCartContext } from '$lib/components/productDetails/addToCartContext';
    import ProductInfo from '$lib/components/productDetails/ProductInfo.svelte';
    import ScrollableList from '$lib/components/listing/ScrollableList.svelte';
    import Breadcrumb from '$lib/components/productDetails/Breadcrumb.svelte';
    import VDPLogoSVG from '$lib/assets/icons/VDPLogoSVG.svelte';
    import StickyNavbar from '$lib/components/navigation/StickyNavbar.svelte';
    import R2Image from '$lib/components/util/R2Image.svelte';
    import { MediaApi } from '$lib/api/mediaApi';
    import VVMVV from '$lib/assets/vectors/VVMVV.svelte';
    import VVMWindrose from '$lib/assets/vectors/VVMWindrose.svelte';
    import VVMO from '$lib/assets/vectors/VVMO.svelte';
    import ProductImages from '$lib/components/productDetails/ProductImages.svelte';
    import { executeAddToCart } from '$lib/util/cartUtil';

    $: productSlug = $page.params.productSlug;

    const shopContext = useShopContext();
    const queryClient = useQueryClient();
    const properties = usePropertiesContext();

    $: productQuery = createQuery({
        queryKey: ['product', productSlug],
        queryFn: ({ queryKey }) => ProductApi.getProductByNumber(queryKey[1], shopContext),
        staleTime: 120 * 1000,
        gcTime: 180 * 1000
    });

    const productStore = writable<ApiSchemas['Product']>();
    let product: ApiSchemas['Product'] | undefined = undefined;
    $: if ($productQuery.data) {
        product = $productQuery.data;
        $productStore = $productQuery.data!;
    }

    $: variantsQuery = createQuery<ApiSchemas['Product']>({
        queryKey: ['variants', product?.parentId],
        queryFn: () => ProductApi.getAllVariantsOfProduct(product.parentId, shopContext),
        staleTime: 120 * 1000,
        gcTime: 180 * 1000
    });
    let variants: ApiSchemas['Product'][] | undefined = undefined;
    $: variants = $variantsQuery.data?.elements ?? undefined;

    $: bannerQuery = createQuery({
        queryKey: ['banner', [product?.translated.customFields.custom_lewo_erp_banner]],
        queryFn: ({ queryKey }) => MediaApi.getMedia({ ids: queryKey[1] as string[] }, shopContext),
        enabled: product?.translated?.customFields?.custom_lewo_erp_banner !== undefined
    });

    let banner: ApiSchemas['Media'] | undefined;
    $: [banner] = $bannerQuery.data ?? [];

    $: cartQuery = createQuery({
        queryKey: ['cart'],
        queryFn: () => CartApi.getCart(shopContext),
        staleTime: 30 * 1000,
        gcTime: 120 * 1000
    });

    let cart: ApiSchemas['Cart'];
    $: cart = $cartQuery.data;

    const addToCartMutation = createMutation<ApiReturnType<'addLineItem'>, unknown, ApiRequestParams<'addLineItem'>>({
        mutationFn: (data) => CartApi.addLineItem(data, shopContext),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] })
    });

    const updateCartMutation = createMutation<
        ApiReturnType<'updateLineItem'>,
        unknown,
        ApiRequestParams<'updateLineItem'>
    >({
        mutationFn: (data) => CartApi.updateLineItem(data, shopContext),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] })
    });

    async function addToCart(quantity: number) {
        const productId = product.id;
        await executeAddToCart(
            productId,
            cart,
            $addToCartMutation,
            $updateCartMutation,
            {
                type: 'product',
                productName: product.translated.name,
                price: product.calculatedPrice.unitPrice,
                currency: shopContext.storeContext.currency.isoCode,
                language: $page.params.language
            },
            quantity
        );
    }

    setContext<AddToCartContext>(ADD_TO_CART_CONTEXT_KEY, {
        addToCart,
        addToCartMutation,
        updateCartMutation,
        product: productStore,
        productSlug
    });

    $: vvmLine = product && properties ? extractLine(product, properties.line) : undefined;

    $: type = product?.properties.find((property: { groupId: string }) => property.groupId === properties.type.id);
    $: taste = product?.properties.find((property: { groupId: string }) => property.groupId === properties.taste.id);
</script>

<svelte:head>
    <title>{product?.metaTitle ?? $t('shop.product.meta.title') + ' ' + createProductTitle(product)}</title>
    <meta
        name="description"
        content={product?.metaDescription ?? $t('shop.product.meta.description') + ' ' + createProductTitle(product)}
    />
    {#if product?.keywords}
        <meta name="keywords" content={product?.keywords} />
    {/if}
    <!-- TODO: Structured Metadata: https://developers.google.com/search/docs/appearance/structured-data/product -->
</svelte:head>

<StickyNavbar isProductPage />

<div class="min-h-screen">
    {#if product && variants}
        {@const beverageCategory = product.translated?.customFields?.custom_lewo_erp_category}
        <div data-theme="van-volxem-{vvmLine}" style="--vvmLine: {vvmLine}" class="flex flex-col font-jost">
            {#if banner}
                <div class="lg:absolute w-full h-[34rem] lg:h-[62rem] pt-28">
                    <R2Image
                        imageId={banner.url}
                        height={1080}
                        widths={[720, 1080, 2560, 3840]}
                        alt={banner.alt}
                        loading="eager"
                        decoding="sync"
                        class="h-full object-cover w-full lg:object-center object-[calc(50%_+_120px)_center]"
                    />
                </div>
            {/if}

            <section
                class="{banner
                    ? 'lg:mb-28'
                    : 'lg:mb-16'} w-full flex lg:flex-row flex-col items-start max-w-screen-2xl mx-auto relative gap-x-3 gap-y-0.5 px-5"
            >
                <div class="absolute top-48 lg:top-[9.5rem] left-5 z-10">
                    <Breadcrumb {product} />
                    {#if product.translated.customFields.custom_lewo_erp_vdp_specs}
                        <div class="text-base-200 mt-5">
                            <VDPLogoSVG />
                        </div>
                    {/if}
                </div>

                <div class="h-full lg:flex hidden w-full lg:max-w-[50%] justify-center mt-32 lg:mt-0 relative">
                    {#if !banner}
                        <div class="absolute left-0 top-0 w-full">
                            {#if vvmLine === 'vv'}
                                <div
                                    class="absolute scale-[55%] lg:scale-90 left-1/2 -translate-x-1/2 top-16 lg:top-[6rem] lg:translate-y-1/2"
                                >
                                    <VVMVV />
                                </div>
                            {:else if vvmLine === 'prestige'}
                                <div
                                    class="absolute scale-50 {banner
                                        ? 'lg:scale-[80%] xl:scale-100 left-1/2 top-0 lg:-top-32 xl:-top-16'
                                        : ' lg:scale-[82%] left-1/2 top-2 lg:-top-10'} -translate-x-1/2 -translate-y-6 lg:translate-y-1/2 opacity-50"
                                >
                                    <VVMWindrose width="590" animate={false} />
                                </div>
                            {:else}
                                <div
                                    class="absolute {banner
                                        ? 'scale-[60%] lg:scale-100 xl:scale-125 left-1/2 top-0 lg:-top-12 xl:top-[2rem]'
                                        : 'scale-[55%] lg:scale-100 left-1/2 top-4'} -translate-x-1/2 translate-y-2 lg:translate-y-1/2"
                                >
                                    <VVMO />
                                </div>
                            {/if}
                        </div>
                        <div class="w-full mt-16 lg:mt-[9.5rem] flex items-start justify-center">
                            <ProductImages {product} productVariant={product} {vvmLine} />
                        </div>
                    {/if}
                </div>

                <div
                    class="w-full mt-[1.5rem] bg-base-100 lg:mt-48 lg:ml-20 lg:p-8 max-w-[37rem] mb-24 lg:mb-4 z-20 relative"
                >
                    <ProductInfo {product} {variants} {vvmLine} />
                </div>
            </section>

            {#if beverageCategory !== 'Paket' && beverageCategory !== 'Ticket'}
                <section class="w-full">
                    <ScrollableList
                        class="!mt-12"
                        properties={[taste?.id]}
                        title={$t('shop.product.similar-in-taste')}
                        exclude={product.parentId}
                    />
                </section>

                <section class="w-full mb-52">
                    <ScrollableList
                        properties={[type?.id]}
                        title={$t('shop.product.bought-in-addition')}
                        exclude={product.parentId}
                    />
                </section>
            {/if}
        </div>
    {/if}
</div>

<style lang="postcss">
    .prestigeDropshadow {
        filter: drop-shadow(-30px -80px 150px rgba(243, 236, 233, 0.2))
            drop-shadow(10px 10px 30px rgba(31, 33, 34, 0.6));
    }

    [data-theme='van-volxem-prestige'] :global(.select) {
        @apply !bg-transparent;
    }

    [data-theme='van-volxem-prestige'] :global(.select-border) {
        @apply border border-primary;
    }

    [data-theme='van-volxem-prestige'] :global(.btn) {
        @apply !text-black;
    }

    [data-theme='van-volxem-prestige'] :global(.bg-darkchamois) {
        @apply !bg-black/25;
    }

    [data-theme='van-volxem-vv'] :global(.bg-darkchamois) {
        @apply !bg-primary/10;
    }

    :global(.product-heading) {
        @apply font-cinzel text-[2rem] lg:text-[2.625rem] leading-[1.19em] tracking-[0.048rem];
    }

    [data-theme='van-volxem-prestige'] :global(.product-heading) {
        @apply font-jost font-light;
    }

    [data-theme='van-volxem-vv'] :global(.product-heading) {
        @apply font-jost font-light;
    }
</style>
